import {generateNKeysBetween} from 'fractional-indexing';
import {useMemo} from 'react';

import {
  type PanelBankConfigWithRefs,
  type PanelPlacementOverride,
  SectionPanelSorting,
  type SectionWithRef,
} from '../../../components/PanelBank/types';
import * as Panels from '../../../util/panels';
import {isSparseOverridesEnabled} from '../../../util/sparseOverridesFeatureFlag';
import {usePart} from '../hooks';
import {usePanelBankSectionConfigs} from '../panelBankSectionConfig/hooks';
import type * as PanelBankConfigTypes from './types';

/**
 * Applies placement override ordering to a section's panels at render time.
 * panelRefs in state stays in canonical (auto-generated) order; this function
 * computes the display order from overrides without mutating state.
 *
 * Positional keys are always derived from the canonical panelRefs order via
 * array index, which is stable because panelRefs is never rearranged.
 */
function applySectionOrdering(
  section: SectionWithRef,
  overrides: Record<string, PanelPlacementOverride> | undefined
): SectionWithRef {
  if (!overrides || Object.keys(overrides).length === 0) {
    return section;
  }

  // Don't apply placement overrides when section is alphabetically sorted —
  // alphabetical sort takes precedence. The reducer clears overrides when
  // alphabetical sort is toggled, but this check provides defense-in-depth
  // and handles the case where cross-section overrides place panels into
  // an alphabetically-sorted section.
  if (section.sorted === SectionPanelSorting.Alphabetical) {
    return section;
  }

  const sectionId = section.__id__;
  const panels = section.panels;

  const hasRelevantOverride = panels.some(panel => {
    const overrideKey = Panels.getOverrideKey(panel);
    return (
      overrideKey != null &&
      Object.hasOwn(overrides, overrideKey) &&
      overrides[overrideKey].sectionId === sectionId
    );
  });

  if (!hasRelevantOverride) {
    return section;
  }

  // Count only panels that DON'T have overrides for positional key generation.
  // This keeps the positional key space stable when panels with overrides
  // (custom panels, duplicates) are added or removed.
  let nonOverriddenCount = 0;
  for (const panel of panels) {
    const overrideKey = Panels.getOverrideKey(panel);
    if (
      !(
        overrideKey != null &&
        Object.hasOwn(overrides, overrideKey) &&
        overrides[overrideKey].sectionId === sectionId
      )
    ) {
      nonOverriddenCount++;
    }
  }

  const positionalKeys = generateNKeysBetween(
    null,
    null,
    nonOverriddenCount || 1
  );

  let positionalIndex = 0;
  const sorted = panels
    .map(panel => {
      const overrideKey = Panels.getOverrideKey(panel);
      let effectiveKey: string;
      if (
        overrideKey != null &&
        Object.hasOwn(overrides, overrideKey) &&
        overrides[overrideKey].sectionId === sectionId
      ) {
        effectiveKey = overrides[overrideKey].orderKey;
      } else {
        effectiveKey = positionalKeys[positionalIndex++];
      }
      return {panel, effectiveKey};
    })
    .sort((a, b) => {
      if (a.effectiveKey < b.effectiveKey) {
        return -1;
      }
      if (a.effectiveKey > b.effectiveKey) {
        return 1;
      }
      return 0;
    })
    .map(entry => entry.panel);

  return {...section, panels: sorted};
}

/**
 * ⚠️ Panel bank denormalization is expensive: this hook returns sections, panels,
 * AND settings (searchQuery, searchHistory, etc.) assembled from normalized state.
 * The `sections` array fetches ALL panels into a nested structure, so any panel
 * or section change triggers recomputation.
 *
 * Avoid triggering unnecessary re-renders by destructuring only what you need.
 * If you only need `settings`, destructure just that property to avoid
 * dependencies on the expensive `sections` array.
 *
 * This is already called and stored in the {@link PanelBankConfigWithRefsProvider} so
 * consider using {@link usePanelBankConfigWithRefs} and grabbing information from the context instead.
 */
export function usePanelBankConfig(
  ref: PanelBankConfigTypes.Ref
): PanelBankConfigWithRefs {
  const part = usePart(ref);
  const sections = usePanelBankSectionConfigs(part.sectionRefs);
  const overrides = part.panelPlacementOverrides;

  // Apply placement override ordering at render time. panelRefs in state
  // stays in canonical order; we sort here for display only.
  const orderedSections = useMemo(() => {
    if (!isSparseOverridesEnabled() || !overrides) {
      return sections;
    }
    return sections.map(section => applySectionOrdering(section, overrides));
  }, [sections, overrides]);

  return useMemo(
    () => ({...part, sections: orderedSections, ref}),
    [ref, part, orderedSections]
  );
}
